import React, { useState } from "react";
import {
  ArrowRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CommentComposer,
  CommentIcon,
  CheckIcon,
  CommentList,
  DiscussIcon,
  StatusPill,
} from "../../StrategyCanvasHelpers";
import { getBlockLabel } from "../../StrategyCanvasStructure";
import useStrategyCanvasReviewStore from "../_redux/StrategyCanvasReviewStore";
import {
  COMMENT_SCOPE,
  REVIEW_STATUS,
} from "../_redux/StrategyCanvasReviewUrls";

// growth_drivers stores a JSON blob (timeframe/revenue/profit/drivers) rather
// than plain text - parse it so the Review Mode accordion can show a
// readable summary instead of raw JSON. Exported so Live Review
// (StrategyCanvasLiveReviewWaiting.js) can render/edit the exact same
// design rather than duplicating it.
export const parseGrowthDrivers = (raw) => {
  if (!raw) return null;
  try {
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    return data && Array.isArray(data.drivers) ? data : null;
  } catch (_error) {
    return null;
  }
};

export const formatDriverValue = (val, unit) => {
  if (val === undefined || val === null || val === "") return "—";
  const normalizedUnit = (unit || "").toString().toLowerCase();
  if (normalizedUnit === "percent") return `${val}%`;

  const num = Number(val);
  if (isNaN(num)) return String(val);

  if (normalizedUnit === "inr") {
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(1)}Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(1)}L`;
    if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
    return `₹${num}`;
  }

  return num.toLocaleString();
};

export const GrowthDriversSummary = ({ value }) => {
  const data = React.useMemo(() => parseGrowthDrivers(value), [value]);

  if (!data) {
    return value ? <p className="rr-description">{value}</p> : null;
  }

  return (
    <div className="rr-growth">
      {data.timeframe && (
        <div className="rr-growth-timeframe">
          <span>{data.timeframe.start || "—"}</span>
          <ArrowRightIcon />
          <span>{data.timeframe.end || "—"}</span>
        </div>
      )}

      <div className="rr-growth-drivers">
        {[
          { label: "Revenue", stat: data.revenue },
          { label: "Profit", stat: data.profit },
        ].map(({ label, stat }) => (
          <div className="rr-growth-driver" key={label}>
            <span className="rr-growth-driver-name">{label}</span>
            <span className="rr-growth-driver-value">
              {formatDriverValue(stat?.start, stat?.unit)}
              <ArrowRightIcon />
              {formatDriverValue(stat?.end, stat?.unit)}
            </span>
          </div>
        ))}

        {data.drivers?.map((driver, index) => (
          <div className="rr-growth-driver" key={`driver-${index}`}>
            <span className="rr-growth-driver-name">
              {driver.name || `Driver ${index + 1}`}
            </span>
            <span className="rr-growth-driver-value">
              {formatDriverValue(driver.start, driver.unit)}
              <ArrowRightIcon />
              {formatDriverValue(driver.end, driver.unit)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// A single reviewable canvas block row - real data from
// GET /coach/strategy-canvas/:uniqueId/review (section 5.2): block_key,
// label, value, review_status ("accepted" | "to_discuss" | null),
// reviewed_at, comments[]. Accept / To-discuss (5.3) and comments (5.4/5.5)
// call the store directly; there's no "un-review" endpoint, so once a
// status is set, clicking the other option just overwrites it.
const ReviewRow = ({
  uniqueId,
  block,
  expanded,
  onToggleExpand,
  readOnly,
  // Reddened because the coach tried to submit with this block unmarked.
  // Defaults false, so the Live Review's own use of this row is unaffected.
  pending = false,
}) => {
  const [isCommenting, setIsCommenting] = useState(false);
  const [draft, setDraft] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingComment, setSavingComment] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const status = block.review_status;
  const comments = block.comments || [];

  const handleSetStatus = async (nextStatus) => {
    if (savingStatus || status === nextStatus) return;
    setSavingStatus(true);
    await useStrategyCanvasReviewStore
      .getState()
      .setBlockStatus(uniqueId, block.block_key, nextStatus);
    setSavingStatus(false);
  };

  // CommentComposer blocks the empty case before this runs.
  const handleSaveComment = async () => {
    if (savingComment) return;
    setSavingComment(true);
    const saved = await useStrategyCanvasReviewStore
      .getState()
      .addComment(uniqueId, {
        scopeType: COMMENT_SCOPE.BLOCK,
        scopeKey: block.block_key,
        commentText: draft.trim(),
      });
    setSavingComment(false);
    if (saved) {
      setDraft("");
      setIsCommenting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    setDeletingId(commentId);
    await useStrategyCanvasReviewStore
      .getState()
      .deleteComment(uniqueId, commentId);
    setDeletingId(null);
  };

  const handleEditComment = async (commentId, commentText) =>
    useStrategyCanvasReviewStore
      .getState()
      .editComment(uniqueId, commentId, commentText);

  return (
    // `data-review-target` is the hook the Complete Review gate scrolls and
    // flashes by, keyed `block-<block_key>`. The block key already identifies a
    // row everywhere else on this screen — status writes, comment scopes,
    // `expandedBlockKey` — so the gate reuses it rather than inventing an id.
    //
    // `rr-row--pending` is only ever set after the coach has TRIED to submit.
    // Every block is unmarked at the start of a review, so reddening them on
    // arrival would paint the whole canvas as errors before the coach has done
    // anything wrong.
    <div
      className={[
        "rr-row",
        status ? `rr-${status}` : "",
        pending ? "rr-row--pending" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-review-target={`block-${block.block_key}`}
    >
      <button className="rr-row-header" onClick={onToggleExpand}>
        <span className="rr-row-left">
          <span className="rr-row-label">{getBlockLabel(block)}</span>
        </span>
        <span className="rr-row-right">
          {status && (
            <StatusPill
              accepted={status === REVIEW_STATUS.ACCEPTED}
              className="rr-status-pill"
              acceptedClassName="rr-status-accepted"
              discussClassName="rr-status-to_discuss"
            />
          )}
          <span className="rr-chevron">
            {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </span>
        </span>
      </button>

      {expanded && (
        <div className="rr-row-body">
          {block.block_key === "growth_drivers" ? (
            <GrowthDriversSummary value={block.value} />
          ) : (
            block.value && <p className="rr-description">{block.value}</p>
          )}

          {!readOnly && (
            <div className="rr-actions">
              <button
                className={`rr-action-btn rr-accept ${status === REVIEW_STATUS.ACCEPTED ? "active" : ""}`}
                onClick={() => handleSetStatus(REVIEW_STATUS.ACCEPTED)}
                disabled={savingStatus}
              >
                <CheckIcon size={12} />
                Accept
              </button>
              <button
                className={`rr-action-btn rr-discuss ${status === REVIEW_STATUS.TO_DISCUSS ? "active" : ""}`}
                onClick={() => handleSetStatus(REVIEW_STATUS.TO_DISCUSS)}
                disabled={savingStatus}
              >
                <DiscussIcon size={12} />
                To discuss with the CEO
              </button>
            </div>
          )}

          {!readOnly && comments.length === 0 && (
            <button
              className="rr-comment-link"
              onClick={() => setIsCommenting((prev) => !prev)}
            >
              <CommentIcon size={12} />
              Add a comment for the CEO
            </button>
          )}

          {comments.length > 0 && (
            <CommentList
              comments={comments}
              onDelete={handleDeleteComment}
              onEdit={handleEditComment}
              deletingId={deletingId}
              readOnly={readOnly}
            />
          )}
          {!readOnly && isCommenting && (
            <CommentComposer
              className="rr-comment-composer"
              value={draft}
              onChange={setDraft}
              onSave={handleSaveComment}
              saving={savingComment}
              placeholder="Add context, a question, or suggested revision"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewRow;
